/**
  Example of markdown tags managed by this utility class (source: https://www.yongliangliu.com/blog/rmark/)
  *This text will be italic*
  **This text will be bold**
  # heading 1
  ## heading 2
  * List 1
  * List 2
  * List 3
  1. One
  2. Two
  3. Three
  [link](https://www.google.com)
  ![image](https://xxx.png)
 */

// The Pattern class is an abstraction that holds the regular expression and the string replacement strategy
// It also provides the method to apply the regular expression
export class Pattern {
  regex: RegExp;
  replacement: string;
  constructor(regex: RegExp, replacement: string) {
    this.regex = regex;
    this.replacement = replacement;
  }

  apply(raw: string): string {
    return raw.replace(this.regex, this.replacement);
  }
}

// Rule class is a collection of patterns that are applied in a sequence
// The reason why we have a collection of patterns is that in Markdown, there can be more than one way to achieve the same formatting
// For example, you can use * or _ to achieve italic text
export class Rule {
  name: string;
  patterns: Pattern[];
  constructor(name: string, patterns: Pattern[]) {
    this.name = name;
    this.patterns = patterns;
  }

  apply(raw: string): string {
    return this.patterns.reduce((result, pattern) => pattern.apply(result), raw);
  }
}

// Collection of Reguar Expressions for the markdown tags

const defaultRules: Rule[] = [
  // HEADINGS - https://www.markdownguide.org/basic-syntax/#headings
  new Rule('header', [
    new Pattern(/^#{6}\s?([^\n]+)/gm, '<h6>$1</h6>'),
    new Pattern(/^#{5}\s?([^\n]+)/gm, '<h5>$1</h5>'),
    new Pattern(/^#{4}\s?([^\n]+)/gm, '<h4>$1</h4>'),
    new Pattern(/^#{3}\s?([^\n]+)/gm, '<h3>$1</h3>'),
    new Pattern(/^#{2}\s?([^\n]+)/gm, '<h2>$1</h2>'),
    new Pattern(/^#{1}\s?([^\n]+)/gm, '<h1>$1</h1>'),
  ]),
  // PARAGRAPH - https://www.markdownguide.org/basic-syntax/#paragraphs-1
  new Rule('paragraph', [new Pattern(/([^\n]+\n?)/g, '\n<p>$1</p>\n')]),
  // BOLD - https://www.markdownguide.org/basic-syntax/#bold
  // new Rule('bold', [new Pattern(/\*\*\s?([^\n]+)\*\*/g, '<b>$1</b>'), new Pattern(/__\s?([^\n]+)__/g, '<b>$1</b>')]),
  new Rule('bold', [new Pattern(/(\*\*|__)(.*?)\1/g, '<b>$2</b>')]),
  // ITALIC - https://www.markdownguide.org/basic-syntax/#italic
  // new Rule('italic', [new Pattern(/\*\s?([^\n]+)\*/g, '<i>$1</i>'), new Pattern(/_\s?([^\n]+)_/g, '<i>$1</i>')]),
  new Rule('italic', [new Pattern(/(\*|_)(.*?)\1/g, '<i>$2</i>')]),
  // IMAGE - https://www.markdownguide.org/basic-syntax/#images-1
  new Rule('image', [new Pattern(/!\[([^\]]+)\]\((\S+)\)/g, '<img src="$2" alt="$1" />')]),
  // LINK - https://www.markdownguide.org/basic-syntax/#links
  new Rule('link', [new Pattern(/\[([^\n]+)\]\(([^\n]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')]),
];

export class RMark {
  private rules: Rule[] = defaultRules;

  public addRuleBefore(rule: Rule, before: string): RMark {
    const index = this.rules.findIndex((r) => r.name === before);
    if (index !== -1) {
      this.rules.splice(index, 0, rule);
    }
    return this;
  }

  public addRule(rule: Rule): RMark {
    this.addRuleBefore(rule, 'paragraph');
    return this;
  }

  public render(raw: string) {
    let result = raw;
    this.rules.forEach((rule) => {
      result = rule.apply(result);
    });
    return result;
  }
}
